<template>
  <div class="stage-level-detail pa-3">
    <div class="caption text--secondary">
      {{ selectedObservatory.address }}
    </div>
    <div class="caption text--secondary">
      {{ selectedObservatory.lat }}, {{ selectedObservatory.lng }}
    </div>

    <div v-if="!isDeficit && !isDeficitRain" class="caption ml-2 my-1">
      （mm/h）
    </div>
    <StageRainChart
      v-if="!isDeficit && !isDeficitRain"
      :height="70"
      :selected-observatory="selectedObservatory"
      :chartTimeSeries="chartTimeSeries"
      :mesuredLatestValue="mesuredLatestValue"
    />
    <v-card height="10" tile flat></v-card>
    <div class="caption ml-2 my-1" v-if="!isDeficit">（{{ levelUnit }}）</div>
    <StageChart
      v-if="!isDeficit"
      :height="240"
      :selected-observatory="selectedObservatory"
      :displayHours="displayHours"
      :chartTimeSeries="chartTimeSeries"
      :mesuredLatestValue="mesuredLatestValue"
      :levelUnit="levelUnit"
    />
    <div class="mt-3" v-if="!isDeficit && !isFloodSensor && !isTide">
      <div class="caption">基準水位</div>
      <div
        class="caption d-flex"
        v-for="std in standardLevels"
        :key="std.label"
      >
        <v-card width="70" flat tile>
          <div
            class="std-color ml-8"
            :style="{ 'border-bottom-color': std.color }"
          ></div>
        </v-card>
        <v-card width="100" flat tile>{{ std.label }}</v-card>
        <v-card width="100" flat tile class="text-right">
          {{
            std.level == undefined
              ? "---"
              : std.level.toFixed(2) + " " + levelUnit
          }}
        </v-card>
      </div>
    </div>
    <v-simple-table dense class="mt-3" v-if="!isDeficit">
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">日時</th>
            <th class="text-left">水位 ({{ levelUnit }})</th>
            <th style="width: 100px"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(ts, index) in timeSeries"
            :key="index"
            :class="{ predict: ts.predict }"
          >
            <td>{{ ts.time }}</td>
            <td class="text-right">{{ ts.level }}</td>
            <td></td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
    <div class="mt-5 caption" v-if="isDeficit">
      この観測所は欠測しています。
    </div>
  </div>
</template>

<script>
import StageChart from "./StageChart";
import StageRainChart from "./StageRainChart";
import { OBSERVATORY, STAGES } from "../../../enums/Type";
import moment from "moment";

export default {
  name: "StageDetail",
  components: {
    StageChart,
    StageRainChart
  },
  data() {
    return {
      displayHours: 1
    };
  },
  computed: {
    selectedObservatory() {
      return this.$store.getters.selectedObservatory;
    },
    isFloodSensor() {
      return (
        this.$store.getters.selectedObservatory.type ===
        OBSERVATORY.FLOODSENSOR.code
      );
    },
    isTide() {
      return (
        this.$store.getters.selectedObservatory.type === OBSERVATORY.TIDE.code
      );
    },
    levelUnit() {
      if (this.isFloodSensor === true) {
        return "cm";
      }
      return this.selectedObservatory.kind === 9 ? "TP.m" : "m";
    },
    mesuredData() {
      if (this.selectedObservatory.timeSeries === undefined) {
        return [];
      }
      return this.selectedObservatory.timeSeries.filter(v => v.type === "real");
    },
    predictData() {
      if (this.selectedObservatory.timeSeries === undefined) {
        return [];
      }
      return this.selectedObservatory.timeSeries.filter(
        v => v.type === "forecast"
      );
    },
    mesuredLatestValue() {
      const date = this.baseDate.clone();
      return date.format("YYYY/MM/DD HH:mm");
    },
    baseDate() {
      return this.$store.getters.baseDate;
    },
    chartTimeSeries() {
      if (this.mesuredLatestValue === null) {
        return [];
      }
      const result = [];
      result.push(this.mesuredLatestValue);
      const latestMoment = moment.utc(
        this.mesuredLatestValue,
        "YYYY/MM/DD HH:mm"
      );
      [...Array(180).keys()].forEach(i => {
        const d = latestMoment.clone().add(-1 * (i + 1), "minutes");
        result.unshift(d.format("YYYY/MM/DD HH:mm"));
      });
      [...Array(this.displayHours * 60).keys()].forEach(i => {
        const d = latestMoment.clone().add(1 * (i + 1), "minutes");
        result.push(d.format("YYYY/MM/DD HH:mm"));
      });
      return result;
    },
    isDeficit() {
      return this.mesuredData.length === 0 && this.predictData.length === 0;
    },
    standardLevels() {
      const levels = [];

      if (this.selectedObservatory.kiki === true) {
        levels.push({
          label: "氾濫発生水位",
          color: STAGES.OUTBREAK.color,
          level: this.selectedObservatory.outbreakLevel
        });
        levels.push({
          label: "氾濫危険水位",
          color: STAGES.DANGEROUS.color,
          level: this.selectedObservatory.dangerousLevel
        });
        levels.push({
          label: "観測開始水位",
          color: STAGES.STANDBY.color,
          level: this.selectedObservatory.standbyLevel
        });
      } else {
        levels.push({
          label: STAGES.OUTBREAK.label,
          color: STAGES.OUTBREAK.color,
          level: this.selectedObservatory.outbreakLevel
        });
        levels.push({
          label: STAGES.DANGEROUS.label,
          color: STAGES.DANGEROUS.color,
          level: this.selectedObservatory.dangerousLevel
        });
        levels.push({
          label: STAGES.EVACUATION.label,
          color: STAGES.EVACUATION.color,
          level: this.selectedObservatory.evacuationLevel
        });
        levels.push({
          label: STAGES.WARNING.label,
          color: STAGES.WARNING.color,
          level: this.selectedObservatory.warningLevel
        });
        levels.push({
          label: STAGES.STANDBY.label,
          color: STAGES.STANDBY.color,
          level: this.selectedObservatory.standbyLevel
        });
      }
      return levels.filter(v => v.level !== undefined);
    },
    timeSeries() {
      const timeSeries = [];
      this.chartTimeSeries.forEach(tsVal => {
        const finded =
          tsVal <= this.mesuredLatestValue
            ? this.mesuredData.find(m => m.date === tsVal)
            : this.predictData.find(p => p.date === tsVal);
        if (finded !== undefined) {
          const level =
            finded.level === undefined ? "---" : finded.level.toFixed(2);
          timeSeries.push({
            time: moment
              .utc(tsVal, "YYYY/MM/DD HH:mm")
              .local()
              .format("MM/DD HH:mm"),
            level: level,
            predict: tsVal > this.mesuredLatestValue
          });
        }
      });
      return timeSeries;
    },
    isDeficitRain() {
      if (this.selectedObservatory.timeSeries === undefined) {
        return true;
      }
      const filtered = this.selectedObservatory.timeSeries.filter(
        v => v.rain !== undefined
      );
      return filtered.length === 0;
    }
  }
};
</script>

<style lang="scss" scoped>
.stage-level-detail {
  thead {
    background-color: #addff3;
  }
  tbody td {
    background: #eee;
  }
  tbody tr:nth-child(odd) td {
    background: #fff;
  }
  .std-color {
    width: 16px;
    height: 12px;
    border-bottom: white 3px solid;
  }
  .predict {
    color: blue;
  }
}
</style>
